<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoicePayments',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    invoice: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const triggerButtonID = ref(null);
  const ux = ref([]);
  const b_6466705138206980_style = computed(() => ({
    'min-width': '150px',
  }));

  const $data = computed(() => ({
    triggerButtonID: triggerButtonID.value,
    ux: ux.value,
    b_6466705138206980_style: b_6466705138206980_style.value,
  }));

  const $$load_triggerButtonID = () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'triggerButtonID' },
      { name: 'ux' },
      { name: 'b_6466705138206980_style', nodeId: '6466705138206980' },
    ],
    loaders: { triggerButtonID: $$load_triggerButtonID },
    ux: {},
    dataRefs: { triggerButtonID, ux, b_6466705138206980_style },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const items = computed(() => {
    return props.invoice.payments.data;
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );

  const act_4592790460410433_emit_6raeuhbz6_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_4592790460410433_close_nprd6lh4z_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.ok;
  };
  const act_4592790460410433_showModal_iukvql9f0_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        oid: $event.scope.item.transaction.id,
      },
    };
  };
  const act_322074727583344_resource_jcwahoddh_getActionArgs = async (
    $event
  ) => {
    return {
      method: 'patch',
      target: props.invoice,
      data: {
        no_payment_required: $event.data.checked,
      },
    };
  };
  const act_5807883900791941_resource_sbcfg0mnu_getActionArgs = async (
    $event
  ) => {
    return {
      method: 'patch',
      target: props.invoice,
      data: {
        uncollectible: $event.data.checked,
      },
    };
  };
  const act_1984986731969919_emit_m9j4uearz_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_1984986731969919_close_fmow3eamg_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.ok;
  };
  const act_1984986731969919_showModal_x7835mxt2_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        transactionKind: 'cash_book',
        transactionType:
          props.invoice.invoice_kind == 'incoming' ? 'payment' : 'deposit',
        invoices: [props.invoice.id],
      },
    };
  };
  const act_298505142589676_emit_mpssj1brc_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_298505142589676_close_w5b3w9evw_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.ok;
  };
  const act_298505142589676_showModal_wdw3aclf8_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        transactionKind: 'bank',
        transactionType:
          props.invoice.invoice_kind == 'incoming' ? 'debit' : 'credit',
        invoices: [props.invoice.id],
      },
    };
  };
  const act_5738488038990219_emit_muz3w4job_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_5738488038990219_close_mn81niwf8_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.ok;
  };
  const act_5738488038990219_showModal_mwtwwaxpt_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        transactionKind: 'technical',
        transactionType: 'invoice_reconciliation',
        invoices: [props.invoice.id],
      },
    };
  };
  const act_7481595298497917_emit_kptnlq8dn_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_7481595298497917_close_ve8oke70c_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.ok;
  };
  const act_7481595298497917_showModal_9ff7zc918_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        transactionKind: 'technical',
        transactionType: 'write_off',
        invoices: [props.invoice.id],
      },
    };
  };

  const act_7315092382398562_reloadComponentData_k82patdnb =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_364gy54se =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_k82patdnb,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_364gy54se],
    event: 'reload',
  });
  const act_4592790460410433_emit_6raeuhbz6 = new actionTypes.EmitAction({
    actionArgs: act_4592790460410433_emit_6raeuhbz6_getActionArgs,
  });
  const inst_evh_4592790460410433_close_nprd6lh4z =
    new actionTypes.EventHandlerImpl({
      action: act_4592790460410433_emit_6raeuhbz6,
      event: 'close',
      when: inst_evh_4592790460410433_close_nprd6lh4z_getWhen,
      displayName: 'emit',
    });
  const act_4592790460410433_showModal_iukvql9f0 =
    new actionTypes.ShowModalAction({
      actionArgs: act_4592790460410433_showModal_iukvql9f0_getActionArgs,
      displayName: 'OpenPayment',
      events: [inst_evh_4592790460410433_close_nprd6lh4z],
    });
  const inst_evh_4592790460410433_click_7vxqd4say =
    new actionTypes.EventHandlerImpl({
      action: act_4592790460410433_showModal_iukvql9f0,
      event: 'click',
      displayName: 'OpenPayment',
    });
  const inst_evh_4592790460410433_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4592790460410433_click_7vxqd4say],
    event: 'click',
  });
  const act_322074727583344_resource_jcwahoddh = new actionTypes.ResourceAction(
    {
      actionArgs: act_322074727583344_resource_jcwahoddh_getActionArgs,
      displayName: 'saveNoPaymentRequired',
    }
  );
  const inst_evh_322074727583344_change_1pxqg8ub9 =
    new actionTypes.EventHandlerImpl({
      action: act_322074727583344_resource_jcwahoddh,
      event: 'change',
      displayName: 'saveNoPaymentRequired',
    });
  const inst_evh_322074727583344_change = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_322074727583344_change_1pxqg8ub9],
    event: 'change',
  });
  const act_5807883900791941_resource_sbcfg0mnu =
    new actionTypes.ResourceAction({
      actionArgs: act_5807883900791941_resource_sbcfg0mnu_getActionArgs,
      displayName: 'saveUncollectible',
    });
  const inst_evh_5807883900791941_change_fw5gmtdi4 =
    new actionTypes.EventHandlerImpl({
      action: act_5807883900791941_resource_sbcfg0mnu,
      event: 'change',
      displayName: 'saveUncollectible',
    });
  const inst_evh_5807883900791941_change = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5807883900791941_change_fw5gmtdi4],
    event: 'change',
  });
  const act_1984986731969919_emit_m9j4uearz = new actionTypes.EmitAction({
    actionArgs: act_1984986731969919_emit_m9j4uearz_getActionArgs,
  });
  const inst_evh_1984986731969919_close_fmow3eamg =
    new actionTypes.EventHandlerImpl({
      action: act_1984986731969919_emit_m9j4uearz,
      event: 'close',
      when: inst_evh_1984986731969919_close_fmow3eamg_getWhen,
      displayName: 'emit',
    });
  const act_1984986731969919_showModal_x7835mxt2 =
    new actionTypes.ShowModalAction({
      actionArgs: act_1984986731969919_showModal_x7835mxt2_getActionArgs,
      displayName: 'createCashPayment',
      events: [inst_evh_1984986731969919_close_fmow3eamg],
    });
  const inst_evh_1984986731969919_clickPrevent_ggqp6r97b =
    new actionTypes.EventHandlerImpl({
      action: act_1984986731969919_showModal_x7835mxt2,
      event: 'click.prevent',
      displayName: 'createCashPayment',
    });
  const inst_evh_1984986731969919_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1984986731969919_clickPrevent_ggqp6r97b],
      event: 'click.prevent',
    });
  const act_298505142589676_emit_mpssj1brc = new actionTypes.EmitAction({
    actionArgs: act_298505142589676_emit_mpssj1brc_getActionArgs,
  });
  const inst_evh_298505142589676_close_w5b3w9evw =
    new actionTypes.EventHandlerImpl({
      action: act_298505142589676_emit_mpssj1brc,
      event: 'close',
      when: inst_evh_298505142589676_close_w5b3w9evw_getWhen,
      displayName: 'emit',
    });
  const act_298505142589676_showModal_wdw3aclf8 =
    new actionTypes.ShowModalAction({
      actionArgs: act_298505142589676_showModal_wdw3aclf8_getActionArgs,
      displayName: 'createBankingPayment',
      events: [inst_evh_298505142589676_close_w5b3w9evw],
    });
  const inst_evh_298505142589676_clickPrevent_4vbm8mmn9 =
    new actionTypes.EventHandlerImpl({
      action: act_298505142589676_showModal_wdw3aclf8,
      event: 'click.prevent',
      displayName: 'createBankingPayment',
    });
  const inst_evh_298505142589676_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_298505142589676_clickPrevent_4vbm8mmn9],
      event: 'click.prevent',
    });
  const act_5738488038990219_emit_muz3w4job = new actionTypes.EmitAction({
    actionArgs: act_5738488038990219_emit_muz3w4job_getActionArgs,
  });
  const inst_evh_5738488038990219_close_mn81niwf8 =
    new actionTypes.EventHandlerImpl({
      action: act_5738488038990219_emit_muz3w4job,
      event: 'close',
      when: inst_evh_5738488038990219_close_mn81niwf8_getWhen,
      displayName: 'emit',
    });
  const act_5738488038990219_showModal_mwtwwaxpt =
    new actionTypes.ShowModalAction({
      actionArgs: act_5738488038990219_showModal_mwtwwaxpt_getActionArgs,
      displayName: 'createReconciliation',
      events: [inst_evh_5738488038990219_close_mn81niwf8],
    });
  const inst_evh_5738488038990219_clickPrevent_2xf0yeb7d =
    new actionTypes.EventHandlerImpl({
      action: act_5738488038990219_showModal_mwtwwaxpt,
      event: 'click.prevent',
      displayName: 'createReconciliation',
    });
  const inst_evh_5738488038990219_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5738488038990219_clickPrevent_2xf0yeb7d],
      event: 'click.prevent',
    });
  const act_7481595298497917_emit_kptnlq8dn = new actionTypes.EmitAction({
    actionArgs: act_7481595298497917_emit_kptnlq8dn_getActionArgs,
  });
  const inst_evh_7481595298497917_close_ve8oke70c =
    new actionTypes.EventHandlerImpl({
      action: act_7481595298497917_emit_kptnlq8dn,
      event: 'close',
      when: inst_evh_7481595298497917_close_ve8oke70c_getWhen,
      displayName: 'emit',
    });
  const act_7481595298497917_showModal_9ff7zc918 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7481595298497917_showModal_9ff7zc918_getActionArgs,
      displayName: 'createBadDebt',
      events: [inst_evh_7481595298497917_close_ve8oke70c],
    });
  const inst_evh_7481595298497917_clickPrevent_thbgq1r5w =
    new actionTypes.EventHandlerImpl({
      action: act_7481595298497917_showModal_9ff7zc918,
      event: 'click.prevent',
      displayName: 'createBadDebt',
    });
  const inst_evh_7481595298497917_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7481595298497917_clickPrevent_thbgq1r5w],
      event: 'click.prevent',
    });

  const actions = {
    inst_evh_7315092382398562_reload_364gy54se,
    inst_evh_7315092382398562_reload,
    inst_evh_4592790460410433_close_nprd6lh4z,
    inst_evh_4592790460410433_click_7vxqd4say,
    OpenPayment: inst_evh_4592790460410433_click_7vxqd4say,
    inst_evh_4592790460410433_click,
    inst_evh_322074727583344_change_1pxqg8ub9,
    saveNoPaymentRequired: inst_evh_322074727583344_change_1pxqg8ub9,
    inst_evh_322074727583344_change,
    inst_evh_5807883900791941_change_fw5gmtdi4,
    saveUncollectible: inst_evh_5807883900791941_change_fw5gmtdi4,
    inst_evh_5807883900791941_change,
    inst_evh_1984986731969919_close_fmow3eamg,
    inst_evh_1984986731969919_clickPrevent_ggqp6r97b,
    createCashPayment: inst_evh_1984986731969919_clickPrevent_ggqp6r97b,
    inst_evh_1984986731969919_clickPrevent,
    inst_evh_298505142589676_close_w5b3w9evw,
    inst_evh_298505142589676_clickPrevent_4vbm8mmn9,
    createBankingPayment: inst_evh_298505142589676_clickPrevent_4vbm8mmn9,
    inst_evh_298505142589676_clickPrevent,
    inst_evh_5738488038990219_close_mn81niwf8,
    inst_evh_5738488038990219_clickPrevent_2xf0yeb7d,
    createReconciliation: inst_evh_5738488038990219_clickPrevent_2xf0yeb7d,
    inst_evh_5738488038990219_clickPrevent,
    inst_evh_7481595298497917_close_ve8oke70c,
    inst_evh_7481595298497917_clickPrevent_thbgq1r5w,
    createBadDebt: inst_evh_7481595298497917_clickPrevent_thbgq1r5w,
    inst_evh_7481595298497917_clickPrevent,
  };

  const evh_7315092382398562_reload_364gy54se = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_364gy54se.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4592790460410433_close_nprd6lh4z = async (event, scope) => {
    return await actions.inst_evh_4592790460410433_close_nprd6lh4z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4592790460410433_click_7vxqd4say = async (event, scope) => {
    return await actions.inst_evh_4592790460410433_click_7vxqd4say.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4592790460410433_click = async (event, scope) => {
    return await actions.inst_evh_4592790460410433_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_322074727583344_change_1pxqg8ub9 = async (event, scope) => {
    return await actions.inst_evh_322074727583344_change_1pxqg8ub9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_322074727583344_change = async (event, scope) => {
    return await actions.inst_evh_322074727583344_change.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5807883900791941_change_fw5gmtdi4 = async (event, scope) => {
    return await actions.inst_evh_5807883900791941_change_fw5gmtdi4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5807883900791941_change = async (event, scope) => {
    return await actions.inst_evh_5807883900791941_change.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1984986731969919_close_fmow3eamg = async (event, scope) => {
    return await actions.inst_evh_1984986731969919_close_fmow3eamg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1984986731969919_clickPrevent_ggqp6r97b = async (event, scope) => {
    return await actions.inst_evh_1984986731969919_clickPrevent_ggqp6r97b.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1984986731969919_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_1984986731969919_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_298505142589676_close_w5b3w9evw = async (event, scope) => {
    return await actions.inst_evh_298505142589676_close_w5b3w9evw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_298505142589676_clickPrevent_4vbm8mmn9 = async (event, scope) => {
    return await actions.inst_evh_298505142589676_clickPrevent_4vbm8mmn9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_298505142589676_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_298505142589676_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5738488038990219_close_mn81niwf8 = async (event, scope) => {
    return await actions.inst_evh_5738488038990219_close_mn81niwf8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5738488038990219_clickPrevent_2xf0yeb7d = async (event, scope) => {
    return await actions.inst_evh_5738488038990219_clickPrevent_2xf0yeb7d.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5738488038990219_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_5738488038990219_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7481595298497917_close_ve8oke70c = async (event, scope) => {
    return await actions.inst_evh_7481595298497917_close_ve8oke70c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7481595298497917_clickPrevent_thbgq1r5w = async (event, scope) => {
    return await actions.inst_evh_7481595298497917_clickPrevent_thbgq1r5w.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7481595298497917_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_7481595298497917_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpBox direction="column">
      <ItpBox direction="column" v-if="items && items.length">
        <ItpBox
          :key="item.id"
          alignItems="start"
          class="border rounded rounded-3 p-2 mb-2 w-100"
          v-for="item in items"
          :style="b_6466705138206980_style"
        >
          <ItpText
            strong
            :text="date(item.transaction.transaction_date)"
            class="me-3"
          >
          </ItpText>
          <ItpBox direction="column" class="me-3">
            <div>
              <ItpLink
                :text="item.transaction.transaction_number"
                strong
                @click="evh_4592790460410433_click($event, { item })"
              >
              </ItpLink>
            </div>
            <UcAccountMiniCard :item="item.account"> </UcAccountMiniCard>
          </ItpBox>
          <ItpBox class="ms-auto">
            <UcPaymentTransactionTypeSymbol
              :transactionType="item.transaction.transaction_type"
              class="mx-2"
            >
            </UcPaymentTransactionTypeSymbol>
            <ItpText strong class="ms-auto">
              {{ number(item.amount) }} {{ item.currency }}
            </ItpText>
          </ItpBox>
        </ItpBox>
      </ItpBox>
      <ItpBox class="px-2 mb-2 w-100" v-if="items && items.length > 1">
        <ItpText>
          {{ pgettext('InvoicePayments', 'Total') }}
        </ItpText>
        <ItpText strong class="ms-auto">
          {{ number(invoice.totals.paid) }} {{ invoice.currency }}
        </ItpText>
      </ItpBox>
      <ItpBox direction="row" alignItems="center">
        <div class="form-check px-0">
          <ItpFormCheckbox
            name="no_payment_required"
            v-model="invoice.no_payment_required"
            is-switch
            :disabled="actions.saveNoPaymentRequired.isRunning"
            class="py-1"
            @change="evh_322074727583344_change($event, {})"
          >
            {{ pgettext('Invoice', 'No payment required') }}
          </ItpFormCheckbox>
          <ItpFormCheckbox
            name="uncollectible"
            v-model="invoice.uncollectible"
            is-switch
            :disabled="actions.saveUncollectible.isRunning"
            class="py-1"
            @change="evh_5807883900791941_change($event, {})"
          >
            {{ pgettext('Invoice', 'Uncollectible') }}
          </ItpFormCheckbox>
        </div>
        <BDropdown right variant="light" class="border, ms-auto">
          <template #button-content>
            <ItpIcon fa="bars" class="mx-1"> </ItpIcon>
            <ItpText :text="pgettext('InvoicePayments', 'New payment')">
            </ItpText>
          </template>
          <BDropdownItem
            @click.prevent="evh_1984986731969919_clickPrevent($event, {})"
          >
            <ItpText :text="pgettext('InvoicePayments', 'New cash payment...')">
            </ItpText>
          </BDropdownItem>
          <BDropdownItem
            @click.prevent="evh_298505142589676_clickPrevent($event, {})"
          >
            <ItpText
              :text="pgettext('InvoicePayments', 'New banking payment...')"
            >
            </ItpText>
          </BDropdownItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <BDropdownItem
            @click.prevent="evh_5738488038990219_clickPrevent($event, {})"
          >
            <ItpText
              :text="pgettext('InvoicePayments', 'New reconciliation...')"
            >
            </ItpText>
          </BDropdownItem>
          <BDropdownItem
            @click.prevent="evh_7481595298497917_clickPrevent($event, {})"
          >
            <ItpText :text="pgettext('InvoicePayments', 'Bad debt...')">
            </ItpText>
          </BDropdownItem>
        </BDropdown>
      </ItpBox>
    </ItpBox>
  </ItpUsercontrol>
</template>
